import React from "react";
import {
    Search,
    Upload,
    Send,
    CircleCheck,
} from "lucide-react";
import { IMAGES } from "../../utils/images";

const steps = [
    {
        number: "01",
        icon: Search,
        title: "Pick a print shop",
        description:
            "Browse nearby shops with service lists, Google reviews, operating hours, and distance. Find the perfect fit in seconds.",
        badge: "Search by name, service, or area",
    },
    {
        number: "02",
        icon: Upload,
        title: "Upload your files",
        description:
            "Drag & drop any file type. Choose Quick Send for speed, or add paper size, quantity, binding, and other print specs.",
        badge: "PDF, DOCX, images, design files",
    },
    {
        number: "03",
        icon: Send,
        title: "Files arrive instantly",
        description:
            "Your files land directly in the shop's dashboard. They review, prepare, and get back to you. No more lost emails.",
        badge: "Direct dashboard delivery",
    },
];

const benefits = [
    "Files upload directly - no size limits, no bounce-backs",
    "Add print specs or quick send",
    "Works on any device - phone, tablet, or desktop",
];

const CustomerJourneySection = () => {
    return (
        <section className="bg-[#f8f8fa]">
            <div className="max-w-[1200px] mx-auto px-[16px] md:px-[40px] py-[56px] md:py-[80px]">
                {/* Header */}
                <div className="text-center mb-[48px]">
                    <p className="font-semibold text-[13px] text-[#ff5323] tracking-[0.5px] uppercase mb-[8px]">
                        For customers
                    </p>

                    <h2 className="font-semibold text-[28px] md:text-[36px] text-[#212326] tracking-[-0.5px] mb-[12px]">
                        From file to finished print in 3 steps
                    </h2>

                    <p className="font-medium text-[16px] text-[#717182] leading-[1.6] max-w-[520px] mx-auto">
                        No accounts, no apps to install, no learning curve. Just pick,
                        upload, and send.
                    </p>
                </div>

                {/* Steps */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-[20px] mb-[48px]">
                    {steps.map((step) => {
                        const Icon = step.icon;

                        return (
                            <div
                                key={step.number}
                                className="relative bg-white rounded-[18px] p-[28px] md:p-[32px] border border-[#e4e5e7] hover:border-[#ffcab9] hover:shadow-[0px_4px_24px_rgba(255,83,35,0.08)] transition-all"
                            >
                                <div className="absolute top-[28px] right-[28px] font-semibold text-[48px] text-[#f4f5f6] leading-none select-none">
                                    {step.number}
                                </div>

                                <div className="w-[52px] h-[52px] rounded-[14px] bg-[#fff0ec] flex items-center justify-center mb-[20px]">
                                    <Icon className="w-[24px] h-[24px] text-[#ff5323]" />
                                </div>

                                <h3 className="font-semibold text-[18px] text-[#212326] mb-[8px]">
                                    {step.title}
                                </h3>

                                <p className="font-medium text-[14px] text-[#717182] leading-[1.6] mb-[16px]">
                                    {step.description}
                                </p>

                                <div className="inline-flex items-center gap-[6px] px-[10px] py-[4px] rounded-[6px] bg-[#f4f5f6]">
                                    <CircleCheck className="w-[12px] h-[12px] text-[#2ecc71]" />
                                    <span className="font-medium text-[11px] text-[#717182]">
                                        {step.badge}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Feature Highlight */}
                <div className="bg-white rounded-[20px] border border-[#e4e5e7] overflow-hidden shadow-[0px_4px_24px_rgba(0,0,0,0.06)]">
                    <div className="grid grid-cols-1 lg:grid-cols-2">
                        {/* Content */}
                        <div className="p-[32px] md:p-[48px] flex flex-col justify-center">
                            <h3 className="font-semibold text-[22px] md:text-[26px] text-[#212326] tracking-[-0.3px] mb-[12px]">
                                The end of messy email attachments
                            </h3>

                            <p className="font-medium text-[15px] text-[#717182] leading-[1.7] mb-[24px]">
                                We've all been there - emailing a 50MB PDF, it bounces back.
                                You compress it, send again, the shop didn't get the attachment.
                                You drive there with a USB.{" "}
                                <span className="font-semibold text-[#212326]">
                                    That era is over.
                                </span>
                            </p>

                            <div className="flex flex-col gap-[12px]">
                                {benefits.map((benefit, index) => (
                                    <div
                                        key={index}
                                        className="flex items-start gap-[10px]"
                                    >
                                        <CircleCheck className="w-[18px] h-[18px] text-[#2ecc71] mt-[2px] shrink-0" />

                                        <p className="font-medium text-[14px] text-[#53585f] leading-[1.5]">
                                            {benefit}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Image */}
                        <div className="relative min-h-[300px] lg:min-h-0">
                            <img
                                src={IMAGES.customerJourney}
                                alt="Team collaborating"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default CustomerJourneySection;
